import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Plus, ClipboardCheck, Trash2, ChevronRight, X, AlertCircle, CheckCircle2, Clock } from 'lucide-react'

function AuditModal({ onClose, onSave, isSaving }) {
  const [form, setForm] = useState({ audit_title: '', auditor: '', scope: '', status: 'Draft', audit_date: '' })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>Schedule New Audit</h2>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Audit Title *</label>
            <input className="form-input" value={form.audit_title} onChange={e => set('audit_title', e.target.value)} placeholder="e.g. Q2 Security Review" />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Auditor</label>
              <input className="form-input" value={form.auditor} onChange={e => set('auditor', e.target.value)} placeholder="Name or Entity" />
            </div>
            <div className="form-group">
              <label className="form-label">Audit Date</label>
              <input type="date" className="form-input" value={form.audit_date} onChange={e => set('audit_date', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Scope</label>
            <textarea className="form-textarea" value={form.scope} onChange={e => set('scope', e.target.value)} placeholder="Boundaries and controls to be audited..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!form.audit_title || isSaving} onClick={() => onSave(form)}>
            {isSaving ? 'Creating…' : 'Schedule Audit'}
          </button>
        </div>
      </div>
    </div>
  )
}

function FindingModal({ auditId, onClose, onSave, isSaving }) {
  const [form, setForm] = useState({ description: '', severity: 'Minor', control_id: '', status: 'Open', corrective_action: '', owner: '', due_date: '' })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>Record Audit Finding</h2>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Finding Description *</label>
            <textarea className="form-textarea" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe the non-conformity or observation..." />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Severity</label>
              <select className="form-select" value={form.severity} onChange={e => set('severity', e.target.value)}>
                {['Critical', 'Major', 'Minor', 'OFI'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Control Reference</label>
              <input className="form-input" value={form.control_id} onChange={e => set('control_id', e.target.value)} placeholder="e.g. 5.1" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Corrective Action Plan</label>
            <textarea className="form-textarea" value={form.corrective_action} onChange={e => set('corrective_action', e.target.value)} placeholder="What needs to be done?" />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Assignee</label>
              <input className="form-input" value={form.owner} onChange={e => set('owner', e.target.value)} placeholder="Owner" />
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input type="date" className="form-input" value={form.due_date} onChange={e => set('due_date', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!form.description || isSaving} onClick={() => onSave(form)}>
            {isSaving ? 'Saving…' : 'Add Finding'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function Audits({ toast }) {
  const qc = useQueryClient()
  const [selectedAudit, setSelectedAudit] = useState(null)
  const [showAuditModal, setShowAuditModal] = useState(false)
  const [showFindingModal, setShowFindingModal] = useState(false)

  const { data: audits = [], isLoading: loadingAudits } = useQuery({ queryKey: ['audits'], queryFn: api.getAudits })
  const { data: findings = [], isLoading: loadingFindings } = useQuery({ 
    queryKey: ['findings', selectedAudit?.id], 
    queryFn: () => api.getAuditFindings(selectedAudit.id),
    enabled: !!selectedAudit 
  })

  const createAuditMut = useMutation({
    mutationFn: api.createAudit,
    onSuccess: () => { qc.invalidateQueries(['audits']); setShowAuditModal(false); toast('Audit scheduled', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const createFindingMut = useMutation({
    mutationFn: (data) => api.createAuditFinding(selectedAudit.id, data),
    onSuccess: () => { qc.invalidateQueries(['findings', selectedAudit?.id]); setShowFindingModal(false); toast('Finding recorded', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const sevColor = (s) => ({
    'Critical': 'var(--risk-critical)', 'Major': 'var(--risk-high)', 
    'Minor': 'var(--risk-medium)', 'OFI': 'var(--risk-low)'
  }[s] || 'var(--text-muted)')

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Audit Management</h1>
          <p>Internal and external audit surveillance and compliance checking</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAuditModal(true)}>
          <Plus size={15} /> Schedule Audit
        </button>
      </div>

      <div className="grid-layout" style={{ gridTemplateColumns: '350px 1fr', gap: 24, display: 'grid' }}>
        {/* Left: Audit List */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface-hover)' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600 }}>Audit Records</h3>
          </div>
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {loadingAudits && <div style={{ padding: 20, textAlign: 'center' }}>Loading...</div>}
            {audits.map(a => (
              <div 
                key={a.id} 
                onClick={() => setSelectedAudit(a)}
                style={{ 
                  padding: '16px 20px', 
                  borderBottom: '1px solid var(--border)', 
                  cursor: 'pointer',
                  background: selectedAudit?.id === a.id ? 'var(--accent-dim)' : 'transparent',
                  borderLeft: selectedAudit?.id === a.id ? '4px solid var(--accent)' : '4px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>AUD-{String(a.id).padStart(3,'0')}</span>
                  <span className={`badge badge-sm ${a.status === 'Completed' ? 'badge-success' : 'badge-info'}`}>{a.status}</span>
                </div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{a.audit_title}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{a.auditor || 'Internal'} • {a.audit_date || 'TBD'}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Findings Detail */}
        <div className="card shadow-sm" style={{ minHeight: 400 }}>
          {!selectedAudit ? (
            <div className="empty-state" style={{ height: '100%', justifyContent: 'center' }}>
              <ClipboardCheck size={48} color="var(--border)" />
              <p>Select an audit record to view findings and evidence.</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: '1.25rem' }}>{selectedAudit.audit_title}</h2>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Status: {selectedAudit.status} | Date: {selectedAudit.audit_date}</p>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowFindingModal(true)}>
                  <Plus size={14} /> Add Finding
                </button>
              </div>

              <div style={{ background: 'var(--bg-base)', padding: 12, borderRadius: 8, marginBottom: 24, fontSize: '0.875rem', border: '1px dashed var(--border)' }}>
                <strong>Scope:</strong> {selectedAudit.scope || 'No scope defined'}
              </div>

              <h3 style={{ fontSize: '1rem', marginBottom: 16 }}>Findings ({findings.length})</h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {loadingFindings ? <div>Loading findings...</div> : findings.map(f => (
                  <div key={f.id} className="card" style={{ padding: 16, border: '1px solid var(--border)', background: 'var(--card-bg)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ 
                          width: 8, height: 8, borderRadius: '50%', 
                          background: sevColor(f.severity)
                        }}></span>
                        <span style={{ fontWeight: 700, fontSize: '0.75rem', color: sevColor(f.severity), letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                          {f.severity}
                        </span>
                        {f.control_id && <span className="badge badge-neutral">Ref: {f.control_id}</span>}
                      </div>
                      <span className={`badge ${f.status === 'Closed' ? 'badge-success' : 'badge-warning'}`}>{f.status}</span>
                    </div>
                    <div style={{ fontSize: '0.9375rem', marginBottom: 12 }}>{f.description}</div>
                    
                    {f.corrective_action && (
                      <div style={{ marginTop: 12, padding: 10, background: 'var(--surface-hover)', borderRadius: 6, fontSize: '0.8125rem' }}>
                        <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <CheckCircle2 size={12} color="var(--success)" /> Corrective Action
                        </div>
                        {f.corrective_action}
                      </div>
                    )}

                    <div className="flex items-center justify-between" style={{ marginTop: 12, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      <div>Owner: {f.owner || '—'}</div>
                      <div>Due: {f.due_date || '—'}</div>
                    </div>
                  </div>
                ))}
                {!loadingFindings && findings.length === 0 && (
                  <div className="empty-state" style={{ background: 'var(--surface-hover)', borderRadius: 12 }}>
                    <p>No findings recorded for this audit.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showAuditModal && (
        <AuditModal 
          onClose={() => setShowAuditModal(false)}
          onSave={data => createAuditMut.mutate(data)}
          isSaving={createAuditMut.isPending}
        />
      )}
      {showFindingModal && (
        <FindingModal
          auditId={selectedAudit.id}
          onClose={() => setShowFindingModal(false)}
          onSave={data => createFindingMut.mutate(data)}
          isSaving={createFindingMut.isPending}
        />
      )}
    </div>
  )
}
